import { UseFormSetError } from 'react-hook-form';

import { LangFormatFunction } from '@/lib/lang';
import { validateAddressExtraFields, validateBCCompanyExtraFields } from '@/shared/service/b2b';
import { Base64 } from '@/utils/base64';
import { validatorRules } from '@/utils/validatorRules';

import { RegisterFields } from './types';

type ContactInformationItems = Array<RegisterFields>;

export const steps = [
  'register.step.account',
  'register.step.details',
  'register.step.finish',
] as const;

export const b2bAddressRequiredFields = [
  'field_country',
  'field_address_1',
  'field_city',
  'field_state',
  'field_zip_code',
];

export const companyAttachmentsFields = (b3lang: LangFormatFunction): ContactInformationItems => [
  {
    name: 'companyAttachments',
    label: b3lang('register.label.companyAttachments'),
    default: [],
    fieldType: 'file',
    required: false,
    xs: 12,
    filesLimit: 3,
    maxFileSize: 10485760, // 10M
  },
];
export interface Country {
  countryCode: string;
  countryName: string;
  id?: string;
  states: [];
}
export interface State {
  stateCode?: string;
  stateName?: string;
  id?: string;
}

type EmailError = {
  [k: number]: string;
};

export const emailError: EmailError = {
  2: 'register.emailValidate.alreadyExitsBC',
  3: 'global.emailValidate.multipleCustomer',
  4: 'global.emailValidate.companyUsed',
  5: 'global.emailValidate.alreadyExits',
  6: 'global.emailValidate.usedSuperAdmin',
};

interface ValidateExtraFieldsProps {
  fields: RegisterFields[];
  data: CustomFieldItems;
  type: 'company' | 'address';
  setError: UseFormSetError<CustomFieldItems>;
}

export const validateExtraFields = async ({
  fields,
  data,
  type,
  setError,
}: ValidateExtraFieldsProps) => {
  return new Promise((resolve, reject) => {
    const init = async () => {
      // Client-side validation for email, phone, and zip code fields
      const fieldsToValidate = fields.filter(
        (item) =>
          item.fieldName?.includes('email') ||
          item.fieldName?.includes('phone') ||
          item.fieldId?.includes('field_email') ||
          item.fieldId?.includes('field_phone') ||
          item.fieldName?.includes('zipCode') ||
          item.name?.includes('zipCode') ||
          item.fieldId?.includes('field_zip'),
      );

      for (const field of fieldsToValidate) {
        const value = data[field.name] || field.default;
        let validationType = '';

        if (field.fieldName?.includes('phone') || field.fieldId?.includes('field_phone')) {
          validationType = 'phone';
        } else if (field.fieldName?.includes('email') || field.fieldId?.includes('field_email')) {
          validationType = 'email';
        } else if (
          field.fieldName?.includes('zipCode') ||
          field.name === 'zipCode' ||
          field.fieldId?.includes('field_zip')
        ) {
          validationType = 'zipCode';
        }

        if (validationType && value) {
          const errorMessage = validatorRules([validationType])(
            value as string,
            (key: string) => key,
          );

          if (errorMessage) {
            setError(field.name, {
              type: 'manual',
              message: errorMessage,
            });
            reject(new Error(`${field.label || field.name}: ${errorMessage}`));
            return;
          }
        }
      }

      // Proceed with server-side validation for custom fields
      const customFields = fields.filter((item) => !!item.custom);

      const extraFields = customFields.map((field: RegisterFields) => ({
        fieldName: Base64.decode(field.name),
        fieldValue: data[field.name] ?? field.default,
      }));

      const fn = type === 'company' ? validateBCCompanyExtraFields : validateAddressExtraFields;

      const result = await fn({
        extraFields,
      });

      if (result.code !== 200) {
        const message = result.data?.errMsg || result.message || '';

        const messageArr = message.split(':');

        if (messageArr.length >= 2) {
          const field = customFields.find((field) => Base64.decode(field.name) === messageArr[0]);
          if (field) {
            setError(field.name, {
              type: 'manual',
              message: messageArr[1],
            });
          }
        }
        reject(message);
      }
      resolve(result);
    };

    init();
  });
};
